/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { monoFont } from './monolith.tokens'

interface PropsType {
	/** 모델 번호 — "6" · "4". 생략하면 라인 이름 "KEDEM" 만 그린다 (/products 라인 행 제목) */
	model?: string
	/** 렌더할 태그. 행 제목에서는 span. */
	as?: 'h1' | 'h2' | 'span' | 'div'
	/** 전체 글자 크기 (px 또는 clamp 문자열). 같은 자리의 KoinosWordmark 와 같은 값을 준다 */
	size?: string | number
	className?: string
	style?: React.CSSProperties
}

/**
 * Iceland 는 capHeight 가 작아 같은 font-size 에서 Science Gothic(KOINOS)보다 작아 보인다.
 * 실측 — Iceland 490/1000 = 0.49, Science Gothic 1400/2000 = 0.70 → 0.70 / 0.49 = 1.43.
 * em 배율이라 어느 크기에 놓이든 자동 적용된다. (SSOT: kedemUpdater/index.html `--kedem-font-scale`)
 */
const KEDEM_FONT_SCALE = 1.43

/**
 * "KEDEM 6" 제품 워드마크.
 *
 * 워드마크 규정(SSOT: kedemUpdater/index.html): Kedem 은 브랜드·모델 전체를 **Iceland 400** 으로
 * 표기한다(가변 축 없음). 자간 .06em, 브랜드-모델 간격 .30em. 폰트는 index.html 의 Google Fonts.
 * `size` 는 옆에 놓이는 KoinosWordmark 와 같은 값을 주면 대문자 높이가 맞는다.
 */
export const KedemWordmark = (props: PropsType) => {
	const Tag = props.as ?? 'span'
	// size 를 안 주면 인라인 font-size 를 아예 두지 않는다 — 인라인 'inherit' 는 className 의 크기를 덮어버린다.
	const sizeStyle: React.CSSProperties =
		props.size === undefined ? {} : { fontSize: props.size }

	return (
		<Tag
			css={style}
			className={props.className}
			style={{ ...sizeStyle, ...props.style }}
			aria-label={props.model === undefined ? 'Kedem' : `Kedem ${props.model}`}
		>
			<span className="kwKedem">KEDEM</span>
			{props.model !== undefined && <span className="kwKedemModel">{props.model}</span>}
		</Tag>
	)
}

const style = css({
	margin: 0,
	lineHeight: 1,
	whiteSpace: 'nowrap',
	display: 'inline-flex',
	alignItems: 'baseline',

	'.kwKedem, .kwKedemModel': {
		fontFamily: monoFont.kedem,
		fontWeight: 400,
		fontVariationSettings: 'normal',
		letterSpacing: '.06em',
		fontSize: `${KEDEM_FONT_SCALE}em`,
		lineHeight: 1,
	},
	'.kwKedemModel': {
		marginLeft: '.30em',
	},
})
